import { Component } from '@theme/component';

let edtCountdownInterval;

function getEdtCountdownTimeString() {
  const now = new Date();
  // EDT is UTC-4
  const edtOffset = -4 * 60;
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const edtDate = new Date(utc + (60000 * edtOffset));

  const targetDate = new Date(edtDate);
  targetDate.setHours(14, 0, 0, 0);

  if (edtDate >= targetDate) {
    targetDate.setDate(targetDate.getDate() + 1);
  }

  const diff = targetDate - edtDate;
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

function updateEdtCountdownTimers() {
  const timerElements = document.querySelectorAll('[data-timer-edt]');
  if (timerElements.length === 0) return;

  const timeString = getEdtCountdownTimeString();
  timerElements.forEach((el) => {
    el.textContent = timeString;
  });
}

function initEdtCountdownTimers() {
  updateEdtCountdownTimers();

  if (edtCountdownInterval) return;

  edtCountdownInterval = setInterval(updateEdtCountdownTimers, 1000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEdtCountdownTimers, { once: true });
} else {
  initEdtCountdownTimers();
}

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Observer that tracks the announcement bar height.
   * @type {ResizeObserver|undefined}
   */
  #resizeObserver = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#initTimer();
    this.#initStickyOffset();
    this.play();
  }

  /**
   * Keeps the `--announcement-bar-height` CSS variable in sync with the rendered
   * height of the announcement bar, so the sticky header can be offset to sit
   * right below it and both stay pinned together while scrolling.
   */
  #initStickyOffset() {
    const bar = this.closest('.announcement-bar');
    if (!bar) return;

    const update = () => {
      document.documentElement.style.setProperty('--announcement-bar-height', `${bar.offsetHeight}px`);
    };

    update();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(update);
      this.#resizeObserver.observe(bar);
    } else {
      window.addEventListener('resize', update);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  #initTimer() {
    initEdtCountdownTimers();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
